// /admin/:resource — one descriptor's rows.
//
// No `mx-auto max-w-7xl px-…` wrapper: AppLayout's <main> already IS that
// container (../../layout/AppLayout.jsx), and repeating it here would nest one
// max-width inside another and double the page padding. Console.jsx carries the
// same note for the same reason.
//
// PRESENTATION, NEVER AUTHORIZATION. Nothing here decides access. `GET /api/admin`
// already omits descriptors the caller's rank cannot reach, and the router's
// per-descriptor `assertRank` refuses the write regardless of what this renders —
// the New button is hidden only so nobody is handed a control whose sole possible
// outcome is a 403.

import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router';

import { apiFetch } from '../../api/client.js';
import { useProductRole } from '../../session/SessionProvider.jsx';
import DescriptorTable from '../../components/admin/DescriptorTable.jsx';
import { canAdminister, canCreateInConsole, consoleErrorMessage } from './descriptor-form.js';
import { supportPath } from '../../support-path.js';

export default function ResourceList() {
  const { resource } = useParams();
  const supportRole = useProductRole('support');
  const [state, setState] = useState({
    status: 'loading', descriptor: null, rows: [], page: null, error: null,
  });

  // A paged resource takes a search term and an offset; every other one ignores both.
  // `term` is what is typed and `applied` is what has been asked for — separated by a
  // debounce so a five-letter search is one request rather than five, on a table that
  // is thousands of rows after the cutover import.
  const [term, setTerm] = useState('');
  const [applied, setApplied] = useState('');
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => {
      setApplied(term);
      // A NEW SEARCH STARTS AT THE FIRST PAGE. Without this, searching from page 3
      // asks for rows 50-75 of a result that has four, and the screen says "no
      // organizations match" about a search that matched some.
      setOffset(0);
    }, 250);
    return () => clearTimeout(timer);
  }, [term]);

  // Typing in the box must not reset the screen to Loading and blank the table — the
  // rows on screen stay until the next answer arrives, which is what makes a search
  // box feel like a filter rather than a navigation.
  useEffect(() => { setTerm(''); setApplied(''); setOffset(0); }, [resource]);

  useEffect(() => {
    // The DESCRIPTOR is kept across a refetch and only the status moves. Blanking it
    // would unmount the search box mid-keystroke on every search — the input would lose
    // focus and the next letter would go nowhere.
    setState((previous) => ({ ...previous, status: 'loading', error: null }));
    const controller = new AbortController();
    // ENCODED, not interpolated raw. `resource` is whatever is in the URL bar, and
    // a `..` segment is resolved by the URL parser BEFORE the request is sent:
    // `/api/admin/../tickets` leaves as `/api/tickets`. Encoding keeps the segment
    // a segment, so an unknown name answers the 404 it should.
    // PARAMS ONLY WHEN THEY ARE NOT THE DEFAULT. A resource that is not paged validates
    // its query as empty and answers 400 on any parameter at all, and on first load this
    // screen does not yet know which kind it is asking about. It does not need to: only
    // a paged descriptor renders the search box and the pager, so only a paged resource
    // can ever have a non-empty term or a non-zero offset to send.
    const params = new URLSearchParams();
    if (applied.trim() !== '') params.set('q', applied.trim());
    if (offset > 0) params.set('offset', String(offset));
    const search = params.toString();
    const path = `/admin/${encodeURIComponent(resource)}${search ? `?${search}` : ''}`;
    Promise.all([
      apiFetch('/admin', { signal: controller.signal }),
      apiFetch(path, { signal: controller.signal }),
    ])
      .then(([index, rows]) => {
        const descriptor = index.data.find((d) => d.name === resource);
        setState(descriptor
          ? { status: 'ready', descriptor, rows: rows.data, page: rows.page ?? null, error: null }
          : {
            status: 'error',
            descriptor: null,
            rows: [],
            page: null,
            error: `No admin resource named "${resource}".`,
          });
      })
      .catch((error) => {
        if (error?.name === 'AbortError') return;
        setState({
          status: 'error', descriptor: null, rows: [], page: null, error: consoleErrorMessage(error),
        });
      });
    return () => controller.abort();
  }, [resource, applied, offset]);

  const mayWrite = state.descriptor ? canAdminister(state.descriptor, supportRole) : false;

  return (
    <div>
      <Link to={supportPath('/admin')} className="text-sm text-ink-secondary hover:underline">
        &larr; Admin
      </Link>

      <div className="mt-2 flex items-center justify-between gap-4">
        <h1 className="text-xl font-semibold text-ink">
          {state.descriptor?.label ?? resource}
        </h1>
        {/* Hidden for a resource whose required fields this generic form cannot author —
            a saved report’s definition is built on /reports/builder. Presentation only,
            exactly like the rank check: the server refuses either way. */}
        {state.status === 'ready' && mayWrite && canCreateInConsole(state.descriptor) && (
          <Link to={supportPath(`/admin/${resource}/new`)} className="btn-primary">
            New
          </Link>
        )}
      </div>

      {/* An error renders a message AND no table — never a message with a
          header-only table beneath it, which reads as "there are zero rows"
          rather than as "we could not ask". */}
      {state.status === 'error' && (
        <div className="mt-4 rounded-lg border border-line-strong bg-sunken p-4 text-sm text-ink">
          {state.error}
        </div>
      )}
      {state.status === 'loading' && <p className="mt-4 text-sm text-ink-muted">Loading…</p>}
      {/* Rendered only for a paged resource. Ten queues do not want a search box, and
          one that filtered a list already fully on screen would be furniture. */}
      {state.descriptor?.paged && (
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <input
            type="search"
            className="field-input max-w-sm"
            placeholder={`Search ${state.descriptor.label.toLowerCase()}…`}
            value={term}
            onChange={(event) => setTerm(event.target.value)}
            aria-label={`Search ${state.descriptor.label}`}
          />
          {state.page && (
            <p className="text-sm text-ink-muted">
              {state.page.total.toLocaleString()}
              {state.page.total === 1 ? ' match' : ' matches'}
            </p>
          )}
        </div>
      )}

      {state.status === 'ready' && (
        <DescriptorTable descriptor={state.descriptor} rows={state.rows} />
      )}

      {/* A pager, and only when there is more than one page of anything. `has_more` is
          computed server-side for the reason lib/pagination.js gives: six clients
          getting `offset + rows.length < total` independently right is optimistic. */}
      {state.descriptor?.paged && state.page && state.page.total > state.rows.length && (
        <div className="mt-3 flex items-center justify-between">
          <p className="text-sm text-ink-muted">
            Showing {(state.page.offset + 1).toLocaleString()}–
            {(state.page.offset + state.rows.length).toLocaleString()} of{' '}
            {state.page.total.toLocaleString()}
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              className="btn-ghost ring-1 ring-line-strong"
              disabled={state.page.offset === 0}
              onClick={() => setOffset(Math.max(0, state.page.offset - state.page.limit))}
            >
              Previous
            </button>
            <button
              type="button"
              className="btn-ghost ring-1 ring-line-strong"
              disabled={!state.page.has_more}
              onClick={() => setOffset(state.page.offset + state.page.limit)}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
